// ===========================================================================
// My community, client side: the card, the /c/CODE landing, the hub section,
// and the panel inside the room.
//
// A community is one persistent private room an adult account holder keeps:
// a name, a six-character code, up to eight members (150 with Room Pro), each
// of whom got in by invitation AND acceptance. Nobody is told of a decline;
// nobody is told of a request that was left alone. The copy here says so,
// because the honest version is the whole point: you ask, the owner lets you
// in when they see it, and that is all anybody learns.
//
// Everything that talks to the server goes through createCommunityClient
// (HTTP, person auth: the account token or the keepsake key) except entering
// the room, which is a socket message app.js sends. The pure helpers at the
// top (when a session starts, the .ics text, the local-time conversion) are
// tested under node; the DOM half is exercised by the build. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { asModal } from './modal.js';
import { el, toast } from '../dom.js';
import { ChatMode, ReportReason } from '@lumi/shared';

/** The landing banner for a /c/CODE link, before there is a session. */
export const COMMUNITY_INVITE_COPY = 'You are invited to a community. Verify your age to ask to join.';
/** What asking means. Said on the landing and again after the ask. */
export const ASK_COPY = 'The owner will let you in when they see it. You will not be told otherwise.';
/** For an adult without an account, on the card. */
export const NO_ACCOUNT_COPY = 'Communities are made by adults with an account. A regular who has one can invite you, and you can ask to join from their link.';
/** How many members a free community holds; the card says so. */
export const FREE_MEMBERS = 8;

const MODE_LABEL = { [ChatMode.VIDEO]: 'Video', [ChatMode.AUDIO]: 'Audio', [ChatMode.TEXT]: 'Text' };
const STATE_LABEL = { owner: 'Host', member: 'Member', invited: 'Invited', requested: 'Asked to join' };
const REASONS = [
  [ReportReason.HARASSMENT, 'Harassment'],
  [ReportReason.NUDITY, 'Nudity'],
  [ReportReason.HATE, 'Hate'],
  [ReportReason.VIOLENCE, 'Violence'],
  [ReportReason.SPAM, 'Spam'],
  [ReportReason.OTHER, 'Other'],
];

// ---- Pure helpers -------------------------------------------------------------

/** The pill text for a member's state. Unknown states show nothing. */
export function stateLabel(state) {
  return STATE_LABEL[state] || '';
}

/** The invite link for a community: the /c/ landing, never the room's /r/. */
export function inviteLink(origin, code) {
  return `${String(origin || '').replace(/\/$/, '')}/c/${String(code || '').toUpperCase()}`;
}

/**
 * "starts in 12 minutes", "starts in 3 hours", "starts in 3 days"; "starting
 * now" inside the first minute; "started 5 minutes ago" while it runs (the
 * server keeps a session listed until it ends). Rounded, never a countdown
 * to the second: this is a calendar, not a stopwatch.
 * @param {string|number} at
 * @param {number} [nowMs]
 */
export function startsIn(at, nowMs = Date.now()) {
  const ms = typeof at === 'number' ? at : Date.parse(at || '');
  if (!Number.isFinite(ms)) return '';
  const diff = ms - nowMs;
  const abs = Math.abs(diff);
  if (abs < 60_000) return 'starting now';
  const mins = Math.round(abs / 60_000);
  let span;
  if (mins < 60) span = `${mins} minute${mins === 1 ? '' : 's'}`;
  else if (mins < 36 * 60) {
    const hours = Math.round(mins / 60);
    span = `${hours} hour${hours === 1 ? '' : 's'}`;
  } else {
    const days = Math.round(mins / (24 * 60));
    span = `${days} day${days === 1 ? '' : 's'}`;
  }
  return diff > 0 ? `starts in ${span}` : `started ${span} ago`;
}

/**
 * The one-line description of a session: "Film night, Sunday 20:00, starts in
 * 3 days". The day and time are the READER's (or the given zone's); the note
 * falls back to "Session". Weekly sessions say so.
 * @param {{ at: string, note?: string, recurrence?: string }} s
 * @param {number} [nowMs]
 * @param {{ timeZone?: string, locale?: string }} [opts]
 */
export function sessionLine(s, nowMs = Date.now(), { timeZone = undefined, locale = 'en-US' } = {}) {
  if (!s || !s.at) return '';
  const d = new Date(s.at);
  if (!Number.isFinite(d.getTime())) return '';
  const day = d.toLocaleDateString(locale, { weekday: 'long', timeZone });
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone });
  const when = s.recurrence === 'weekly' ? `every ${day} ${time}` : `${day} ${time}`;
  return `${s.note || 'Session'}, ${when}, ${startsIn(s.at, nowMs)}`;
}

/**
 * A date ("2026-09-13") and a time ("20:00") typed in the reader's own clock,
 * as an ISO instant. The browser's Date parses "YYYY-MM-DDTHH:MM" as LOCAL
 * time, which is the point: "Sunday 20:00, your time".
 * @returns {string|null}
 */
export function localToIso(dateStr, timeStr) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr || '')) || !/^\d{2}:\d{2}$/.test(String(timeStr || ''))) return null;
  const d = new Date(`${dateStr}T${timeStr}`);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

function icsStamp(ms) {
  return new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

function icsEscape(s) {
  return String(s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
}

/**
 * An iCalendar file for one session, built from the row and nothing else:
 * the community's name, the note, the first date in UTC, the length, a weekly
 * rule when it repeats, and the invite link. Lines end CRLF as the format
 * asks. Nothing personal is in it -- no members, no owner.
 * @param {{ id?: string, name: string, note?: string, at: string, minutes: number, recurrence?: string, code?: string, origin?: string, firstAt?: string }} s
 */
export function buildIcs(s) {
  const startMs = Date.parse(s.firstAt || s.at || '');
  if (!Number.isFinite(startMs)) return '';
  const mins = Math.max(1, Math.round(Number(s.minutes) || 60));
  const summary = s.note ? `${s.name}: ${s.note}` : s.name;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Lumi//Community sessions//EN',
    'BEGIN:VEVENT',
    `UID:${icsEscape(s.id || `lumi-${startMs}`)}@lumichat.io`,
    `DTSTAMP:${icsStamp(Date.now())}`,
    `DTSTART:${icsStamp(startMs)}`,
    `DURATION:PT${mins}M`,
    `SUMMARY:${icsEscape(summary)}`,
  ];
  if (s.recurrence === 'weekly') lines.push('RRULE:FREQ=WEEKLY');
  if (s.code && s.origin) lines.push(`URL:${inviteLink(s.origin, s.code)}`);
  lines.push(`DESCRIPTION:${icsEscape(`A session of ${s.name} on Lumi.`)}`, 'END:VEVENT', 'END:VCALENDAR');
  return `${lines.join('\r\n')}\r\n`;
}

/** "3 members" / "1 member" / "no members yet" -- the owner is not counted. */
export function memberCountText(n) {
  const k = Number(n) || 0;
  if (k === 0) return 'no members yet';
  return `${k} member${k === 1 ? '' : 's'}`;
}

/** "2 here" / "nobody here right now". */
export function hereText(n) {
  const k = Number(n) || 0;
  return k ? `${k} here` : 'nobody here right now';
}

// ---- The HTTP client -------------------------------------------------------------

/**
 * @param {{ base?: string, headers?: () => Record<string, string>, fetchImpl?: typeof fetch }} [opts]
 *   `headers` answers with the person auth of the moment: the account token
 *   when signed in, the keepsake key when there is one, both when both.
 */
export function createCommunityClient({ base = '', headers = () => ({}), fetchImpl = globalThis.fetch } = {}) {
  async function call(method, path, body) {
    const h = { ...headers() };
    if (body !== undefined) h['Content-Type'] = 'application/json';
    try {
      const r = await fetchImpl(`${base}${path}`, { method, headers: h, body: body === undefined ? undefined : JSON.stringify(body) });
      const data = await r.json().catch(() => ({}));
      return { status: r.status, data };
    } catch {
      return { status: 0, data: { error: 'offline' } };
    }
  }
  const id = (v) => encodeURIComponent(String(v));
  return {
    mine: () => call('GET', '/api/community/mine'),
    create: ({ name, mode }) => call('POST', '/api/community', { name, mode }),
    byCode: (code) => call('GET', `/api/community/by-code/${id(code)}`),
    request: (code, handle) => call('POST', `/api/community/by-code/${id(code)}/request`, handle ? { handle } : {}),
    rename: (cid, name) => call('POST', `/api/community/${id(cid)}/rename`, { name }),
    invite: (cid, personId) => call('POST', `/api/community/${id(cid)}/invite`, { personId }),
    accept: (cid) => call('POST', `/api/community/${id(cid)}/accept`, {}),
    acceptRequest: (cid, personId) => call('POST', `/api/community/${id(cid)}/accept`, { personId }),
    decline: (cid) => call('POST', `/api/community/${id(cid)}/decline`, {}),
    leave: (cid, { block = false } = {}) => call('POST', `/api/community/${id(cid)}/leave`, { block: !!block }),
    remove: (cid, personId) => call('POST', `/api/community/${id(cid)}/remove`, { personId }),
    addSession: (cid, s) => call('POST', `/api/community/${id(cid)}/sessions`, s),
    deleteSession: (cid, sid) => call('DELETE', `/api/community/${id(cid)}/sessions/${id(sid)}`),
  };
}

/** What a refusal means on screen. Codes the client did not expect say something plain. */
export function errorText(code) {
  switch (code) {
    case 'already_owner': return 'You already have a community.';
    case 'account_required': return 'Communities are made by adults with an account. Sign in first.';
    case 'name_required': return 'Give it a name.';
    case 'not_a_regular': return 'You can only invite your regulars.';
    case 'owner_only': return 'Only the host can do that.';
    case 'not_available': return 'Communities are not available for you yet.';
    case 'not_allowed': return 'That is not possible for this community.';
    case 'cannot_refuse': return 'A request cannot be refused; leave it, or block the person.';
    case 'too_many_requests': return 'Slow down a moment.';
    case 'in_the_past': return 'That time has already passed.';
    case 'bad_time': return 'Pick a date and a time.';
    case 'bad_minutes': return 'Between five minutes and twelve hours.';
    case 'too_far': return 'Within the next year, please.';
    case 'too_many': return 'That is as many sessions as a calendar holds.';
    case 'offline': return 'Could not reach Lumi. Try again in a moment.';
    default: return 'That did not work. Try again.';
  }
}

// ---- DOM: shared bits -------------------------------------------------------------

function pill(state) {
  const label = stateLabel(state);
  return label ? el('span', { class: `state-pill state-${state}`, text: label }) : null;
}

function copyLinkButton(code, { small = true } = {}) {
  return el('button', {
    type: 'button',
    class: `btn btn-ghost ${small ? 'btn-xs' : 'btn-sm'}`,
    text: t('Copy Lumi Link'),
    title: t('Anyone with the link can verify their age and ask to join. You decide who gets in.'),
    onClick: () => {
      const link = inviteLink(location.origin, code);
      navigator.clipboard?.writeText(link).then(
        () => toast('Lumi Link copied. Whoever opens it can ask to join; you decide.'),
        () => toast(`Share this link: ${link}`),
      );
    },
  });
}

function downloadIcs(text, filename) {
  try {
    const blob = new Blob([text], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = el('a', { href: url, download: filename });
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch {
    toast('Could not build the calendar file.');
  }
}

/** The moderator-style prompt for the owner's fifteen-minute kick: a reason is required. */
function promptKick(onKick, peerId) {
  const choice = window.prompt(
    'Remove this person from the room for 15 minutes.\nYou must cite a reason: type the number:\n' +
      REASONS.map(([, label], i) => `${i + 1}. ${label}`).join('\n'),
  );
  if (choice == null) return;
  const idx = Number(choice) - 1;
  if (!Number.isInteger(idx) || !REASONS[idx]) return;
  const note = window.prompt('Add a short note for the admin (optional):') || '';
  onKick(peerId, REASONS[idx][0], note.slice(0, 500));
}

/** The session list with the .ics link, and Delete for the owner. */
function sessionsList(sessions, { name, code, isOwner, onDelete }) {
  const list = Array.isArray(sessions) ? sessions : [];
  if (!list.length) return el('p', { class: 'muted community-empty', text: isOwner ? 'No sessions planned. Add one below: "same people, same time" is what keeps a room alive.' : 'No sessions planned yet.' });
  return el(
    'ul',
    { class: 'community-sessions' },
    list.map((s) =>
      el('li', { class: 'community-session' }, [
        el('span', { class: 'community-session-line', text: sessionLine(s) }),
        el('span', { class: 'community-session-actions' }, [
          el('button', {
            type: 'button',
            class: 'btn btn-ghost btn-xs',
            text: t('Add to calendar'),
            title: t('Download an .ics file for this session'),
            onClick: () => downloadIcs(buildIcs({ ...s, name, code, origin: location.origin }), `${(s.note || 'session').replace(/[^A-Za-z0-9]+/g, '-').toLowerCase() || 'session'}.ics`),
          }),
          isOwner && onDelete
            ? el('button', { type: 'button', class: 'btn btn-ghost btn-xs', text: t('Delete'), onClick: () => onDelete(s.id) })
            : null,
        ].filter(Boolean)),
      ]),
    ),
  );
}

/** The owner's add-a-session form: a date, a local time, a length, a note, once or weekly. */
function sessionForm(onAdd) {
  const today = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const date = el('input', { type: 'date', value: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`, 'aria-label': 'Date' });
  const time = el('input', { type: 'time', value: '20:00', 'aria-label': 'Time, your clock' });
  const minutes = el('select', { 'aria-label': 'How long' }, [30, 45, 60, 90, 120, 180].map((m) => el('option', { value: String(m), text: m < 60 ? `${m} min` : `${m / 60} h`, selected: m === 60 ? '' : null })));
  const note = el('input', { type: 'text', maxlength: '80', placeholder: t('What is it? e.g. Film night'), 'aria-label': 'Note' });
  const weekly = el('select', { 'aria-label': 'Repeats' }, [el('option', { value: 'once', text: t('Once') }), el('option', { value: 'weekly', text: t('Every week') })]);
  const err = el('div', { class: 'error', role: 'alert' });
  const add = el('button', {
    type: 'button',
    class: 'btn btn-secondary btn-sm',
    text: t('Add session'),
    onClick: async () => {
      const at = localToIso(date.value, time.value);
      if (!at) {
        err.textContent = errorText('bad_time');
        return;
      }
      add.disabled = true;
      err.textContent = '';
      const r = await onAdd({ at, minutes: Number(minutes.value), note: note.value.trim(), recurrence: weekly.value });
      add.disabled = false;
      if (r && r.error) err.textContent = errorText(r.error);
      else note.value = '';
    },
  });
  return el('div', { class: 'community-session-form' }, [
    el('div', { class: 'community-form-row' }, [date, time, minutes, weekly]),
    el('div', { class: 'community-form-row' }, [note, add]),
    el('p', { class: 'muted community-hint', text: t('Times are in your clock. Members see it inside the room and get a reminder ten minutes before, if they are on Lumi then.') }),
    err,
  ]);
}

// ---- The card: in the regulars panel ----------------------------------------------
//
// One card, three shapes: yours (create it, or run it), the ones you are in,
// and the invitations waiting for you. Loaded over HTTP, so it works from the
// landing with no socket; only "Open" needs one, and without one it primes
// the next start instead.

/**
 * @param {object} h
 * @param {ReturnType<typeof createCommunityClient>} h.client
 * @param {() => boolean} h.connected      is there a socket to open a room through
 * @param {() => Array<{ personId: string, handle: string|null }>} h.regulars  for the invite picker
 * @param {(code: string) => void} h.onOpen  enter the community's room (COMMUNITY_JOIN), or prime it
 * @param {() => void} [h.onChanged]  something about the community changed --
 *   created, renamed, a member accepted or removed. The hub draws the same
 *   community from its own copy of /mine, and without this it kept showing
 *   "Start a community" to somebody who had just started one, until a reload.
 * @returns {{ root: HTMLElement, api: { refresh: () => Promise<void> } }}
 */
export function renderCommunityCard(h) {
  const root = el('div', { class: 'keepsake-block community-card' });
  const ignored = new Set(); // requests the owner chose to look past this time; nothing is sent
  let mine = null;
  let loading = true;
  let failed = null;

  async function refresh() {
    const r = await h.client.mine();
    loading = false;
    if (r.status === 200) {
      mine = r.data;
      failed = null;
    } else {
      mine = null;
      failed = r.status === 401 ? 'unauthorized' : r.data?.error || 'unavailable';
    }
    render();
  }

  async function act(p, { silent = false } = {}) {
    const r = await p;
    if (r.status >= 200 && r.status < 300) {
      await refresh();
      // Every mutation this card makes goes through here, so this is the one
      // place anything else drawing the same community needs telling.
      h.onChanged?.();
      return r.data;
    }
    if (!silent) toast(errorText(r.data?.error));
    return { error: r.data?.error || 'unavailable' };
  }

  function openButton(code, { primary = false } = {}) {
    return el('button', {
      type: 'button',
      class: `btn ${primary ? 'btn-primary' : 'btn-secondary'} btn-sm`,
      text: t('Open'),
      title: h.connected() ? 'Go to the room' : 'Start Lumi and your community opens',
      onClick: () => h.onOpen(code),
    });
  }

  function createForm() {
    const name = el('input', { type: 'text', maxlength: '40', placeholder: t('e.g. Tuesday book club'), 'aria-label': 'Community name' });
    const mode = el('select', { 'aria-label': 'Kind of room' }, [
      el('option', { value: ChatMode.VIDEO, text: t('Video room') }),
      el('option', { value: ChatMode.AUDIO, text: t('Audio room') }),
      el('option', { value: ChatMode.TEXT, text: t('Text room') }),
    ]);
    const err = el('div', { class: 'error', role: 'alert' });
    const btn = el('button', {
      type: 'button',
      class: 'btn btn-primary btn-sm',
      text: t('Start my community'),
      onClick: async () => {
        if (!name.value.trim()) {
          err.textContent = errorText('name_required');
          return;
        }
        btn.disabled = true;
        const r = await act(h.client.create({ name: name.value.trim(), mode: mode.value }), { silent: true });
        btn.disabled = false;
        if (r && r.error) err.textContent = errorText(r.error);
      },
    });
    return el('div', { class: 'community-create' }, [
      el('p', { class: 'muted', text: `One private room that is still there when you come back, for up to ${FREE_MEMBERS} people you invite. Each of them says yes before they are in.` }),
      el('div', { class: 'community-form-row' }, [name, mode]),
      el('div', { class: 'buy-row' }, [btn]),
      err,
    ]);
  }

  function ownedBlock(c) {
    const members = (c.members || []).filter((m) => m.state === 'member');
    const invited = (c.members || []).filter((m) => m.state === 'invited');
    const requests = (c.members || []).filter((m) => m.state === 'requested' && !ignored.has(m.personId));
    const inList = new Set((c.members || []).map((m) => m.personId));
    const candidates = (h.regulars() || []).filter((r) => r && r.personId && !inList.has(r.personId));

    const nameRow = el('div', { class: 'community-head' }, [
      el('strong', { class: 'community-name', text: c.name }),
      el('span', { class: 'room-code-pill', title: t('Your community code'), text: c.code }),
      c.pro ? el('span', { class: 'plus-badge', title: t('Room Pro: the larger room'), text: t('PRO') }) : null,
    ].filter(Boolean));

    const rename = el('button', {
      type: 'button',
      class: 'btn btn-ghost btn-xs',
      text: t('Rename'),
      onClick: async () => {
        const next = window.prompt('A new name for your community:', c.name);
        if (next == null || !next.trim() || next.trim() === c.name) return;
        await act(h.client.rename(c.id, next.trim()));
      },
    });

    const memberRow = (m) =>
      el('div', { class: 'community-row' }, [
        el('span', { class: `here-dot${m.here ? ' on' : ''}`, 'aria-hidden': 'true' }),
        el('span', { class: 'community-row-name', text: m.handle || 'Someone' }),
        pill(m.state),
        el('span', { class: 'community-row-actions' }, [
          m.state === 'requested'
            ? el('button', { type: 'button', class: 'btn btn-primary btn-xs', text: t('Accept'), onClick: () => act(h.client.acceptRequest(c.id, m.personId)) })
            : null,
          m.state === 'requested'
            ? el('button', {
                type: 'button',
                class: 'btn btn-ghost btn-xs',
                text: t('Ignore'),
                title: t('Hides it here for now. They are not told, and they can still be accepted later.'),
                onClick: () => {
                  ignored.add(m.personId);
                  render();
                },
              })
            : null,
          m.state === 'invited'
            ? el('button', { type: 'button', class: 'btn btn-ghost btn-xs', text: t('Withdraw'), title: t('Take the invitation back'), onClick: () => act(h.client.remove(c.id, m.personId)) })
            : null,
          m.state === 'member'
            ? el('button', {
                type: 'button',
                class: 'btn btn-ghost btn-xs',
                text: t('Remove'),
                title: t('They leave the room and cannot come back unless you invite them again'),
                onClick: () => {
                  if (!window.confirm(`Remove ${m.handle || 'this person'} from ${c.name}? They will be told they are no longer a member.`)) return;
                  act(h.client.remove(c.id, m.personId));
                },
              })
            : null,
        ].filter(Boolean)),
      ]);

    let invitePicker = null;
    if (candidates.length) {
      const pick = el('select', { 'aria-label': 'A regular to invite' }, [
        el('option', { value: '', text: t('Invite a regular...') }),
        ...candidates.map((r) => el('option', { value: r.personId, text: r.handle || 'Someone you talked to' })),
      ]);
      pick.addEventListener('change', async () => {
        const personId = pick.value;
        if (!personId) return;
        pick.disabled = true;
        await act(h.client.invite(c.id, personId));
        pick.disabled = false;
      });
      invitePicker = el('div', { class: 'community-form-row' }, [pick]);
    }

    return el('div', { class: 'community-owned' }, [
      nameRow,
      el('div', { class: 'buy-row community-toolbar' }, [
        openButton(c.code, { primary: true }),
        copyLinkButton(c.code),
        rename,
      ]),
      el('p', { class: 'muted community-hint', text: `${memberCountText(members.length)} of ${c.capacity || FREE_MEMBERS}, ${hereText(c.here)}. Anyone you invite, or who asks from your Lumi Link, joins only when both of you have said yes.` }),
      requests.length ? el('strong', { class: 'community-sub', text: t('Asked to join') }) : null,
      ...requests.map(memberRow),
      el('strong', { class: 'community-sub', text: t('Members') }),
      members.length || invited.length
        ? el('div', { class: 'community-members' }, [...members, ...invited].map(memberRow))
        : el('p', { class: 'muted community-empty', text: t('Nobody yet. Invite a regular, or share your Lumi Link.') }),
      invitePicker || (h.regulars().length ? null : el('p', { class: 'muted community-hint', text: t('You can invite your regulars once you have some: wave after a chat, and if they wave back, they are here to pick.') })),
      el('strong', { class: 'community-sub', text: t('Sessions') }),
      sessionsList(c.sessions, { name: c.name, code: c.code, isOwner: true, onDelete: (sid) => act(h.client.deleteSession(c.id, sid)) }),
      sessionForm((s) => act(h.client.addSession(c.id, s), { silent: true })),
    ].filter(Boolean));
  }

  function membershipRow(m) {
    const next = (m.sessions || [])[0];
    return el('div', { class: 'community-row community-membership' }, [
      el('div', { class: 'community-row-main' }, [
        el('div', {}, [el('strong', { text: m.name }), m.state === 'requested' ? pill('requested') : null].filter(Boolean)),
        el('span', { class: 'muted community-row-meta', text: m.state === 'requested' ? ASK_COPY : `${hereText(m.here)}${next ? `. ${sessionLine(next)}` : ''}` }),
      ]),
      el('span', { class: 'community-row-actions' }, [
        m.state === 'member' ? openButton(m.code) : null,
        el('button', {
          type: 'button',
          class: 'btn btn-ghost btn-xs',
          text: m.state === 'requested' ? 'Withdraw' : 'Leave',
          onClick: () => {
            if (m.state === 'member' && !window.confirm(`Leave ${m.name}? You would need a new invitation to come back.`)) return;
            act(h.client.leave(m.id));
          },
        }),
        m.state === 'member'
          ? el('button', {
              type: 'button',
              class: 'btn btn-ghost btn-xs',
              text: t('Leave and block'),
              title: t('Leave, and block the host: you stop being regulars and they cannot invite you again. They are not told.'),
              onClick: () => {
                if (!window.confirm(`Leave ${m.name} and block its host? You will no longer be regulars, and they are told nothing.`)) return;
                act(h.client.leave(m.id, { block: true }));
              },
            })
          : null,
      ].filter(Boolean)),
    ]);
  }

  function invitationRow(inv) {
    return el('div', { class: 'community-row community-invitation' }, [
      el('div', { class: 'community-row-main' }, [
        el('strong', { text: inv.name }),
        el('span', { class: 'muted community-row-meta', text: `${inv.from ? `${inv.from} invited you` : 'You are invited'}. ${MODE_LABEL[inv.mode] || 'Video'} room. If you say no, nobody is told.` }),
      ]),
      el('span', { class: 'community-row-actions' }, [
        el('button', { type: 'button', class: 'btn btn-primary btn-xs', text: t('Accept'), onClick: () => act(h.client.accept(inv.id)) }),
        el('button', { type: 'button', class: 'btn btn-ghost btn-xs', text: t('Decline'), onClick: () => act(h.client.decline(inv.id)) }),
      ]),
    ]);
  }

  function render() {
    root.replaceChildren(el('strong', { text: t('My community') }));
    if (loading) {
      root.append(el('p', { class: 'muted', text: t('Loading...') }));
      return;
    }
    if (failed === 'unauthorized') {
      root.append(el('p', { class: 'muted', text: t('Start a chat once and Lumi will remember you on this device; then a community can too.') }));
      return;
    }
    if (failed === 'not_available') {
      root.append(el('p', { class: 'muted', text: errorText('not_available') }));
      return;
    }
    if (failed) {
      root.append(el('p', { class: 'muted', text: errorText(failed) }), el('div', { class: 'buy-row' }, [el('button', { type: 'button', class: 'btn btn-ghost btn-sm', text: t('Try again'), onClick: refresh })]));
      return;
    }
    const invitations = mine.invitations || [];
    const memberships = mine.memberships || [];
    if (invitations.length) {
      root.append(el('strong', { class: 'community-sub', text: invitations.length === 1 ? 'An invitation' : 'Invitations' }), ...invitations.map(invitationRow));
    }
    if (mine.owned) root.append(ownedBlock(mine.owned));
    else if (mine.canOwn) root.append(createForm());
    else root.append(el('p', { class: 'muted', text: NO_ACCOUNT_COPY }));
    if (memberships.length) {
      root.append(el('strong', { class: 'community-sub', text: t('Communities you are in') }), ...memberships.map(membershipRow));
    }
    root.append(el('p', { class: 'muted community-foot', text: t('Lumi keeps the name, the code, who is in, and the sessions. Not the conversation.') }));
  }

  render();
  refresh();
  return { root, api: { refresh } };
}

// ---- The /c/CODE landing ------------------------------------------------------------
//
// Opened once there is a session, over the rooms hub: the community's name
// and size, and one button. The copy says exactly what asking does and does
// not do. Somebody already in is not shown this at all (app.js opens the
// room instead).

/**
 * @param {object} h
 * @param {string} h.code
 * @param {{ name: string, members: number, mode?: string, you: string }} h.info
 * @param {() => Promise<{ status: number, data: object }>} h.onAsk      POST the request
 * @param {() => Promise<{ status: number, data: object }>} [h.onAccept] accept an invitation (when you === 'invited')
 * @param {() => void} [h.onOpen]   enter the room after accepting
 * @param {() => void} [h.onClose]
 */
export function openCommunityLanding(h) {
  const body = el('div', { class: 'billing-body community-landing' });
  const modal = el('div', { class: 'billing-modal account-modal', role: 'dialog', 'aria-label': 'A community invitation' }, [
    el('div', { class: 'billing-head' }, [
      el('strong', { text: t('A community') }),
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Close'), onClick: () => close() }),
    ]),
    body,
    el('div', { class: 'billing-foot muted', text: t('A community is one private room somebody keeps for people they have invited. Lumi stores who is in it and the sessions planned, nothing else.') }),
  ]);
  function close() {
    dialog.close();
    h.onClose?.();
  }
  const info = h.info || {};
  const modeText = MODE_LABEL[info.mode] || 'Video';
  const status = el('p', { class: 'community-landing-status' });
  const ask = el('button', {
    type: 'button',
    class: 'btn btn-primary',
    text: info.you === 'invited' ? 'Accept and join' : 'Ask to join',
    onClick: async () => {
      ask.disabled = true;
      if (info.you === 'invited' && h.onAccept) {
        const r = await h.onAccept();
        if (r.status === 200) {
          close();
          h.onOpen?.();
          return;
        }
        ask.disabled = false;
        status.textContent = errorText(r.data?.error);
        return;
      }
      const r = await h.onAsk();
      if (r.status === 200) {
        if (r.data?.you === 'member') {
          close();
          h.onOpen?.();
          return;
        }
        ask.hidden = true;
        status.textContent = `You asked to join ${info.name}. ${ASK_COPY} Check "My community" in your regulars panel later.`;
        return;
      }
      ask.disabled = false;
      status.textContent = r.status === 403 && r.data?.error === 'not_allowed' ? 'You cannot ask to join this one again.' : errorText(r.data?.error);
    },
  });
  const alreadyAsked = info.you === 'requested';
  const terminal = info.you === 'left' || info.you === 'removed';
  body.append(
    el('div', { class: 'community-head' }, [el('strong', { class: 'community-name', text: info.name || 'A community' }), el('span', { class: 'room-code-pill', text: String(h.code || '').toUpperCase() })]),
    el('p', { class: 'muted', text: `${modeText} room, ${memberCountText(info.members)}.` }),
    el('p', {
      text: alreadyAsked
        ? `You have already asked to join. ${ASK_COPY}`
        : terminal
          ? 'You were in this community before. Only a new invitation from its host can bring you back.'
          : info.you === 'invited'
            ? 'You have been invited. Accept, and you are in.'
            : `Ask to join. ${ASK_COPY}`,
    }),
    alreadyAsked || terminal ? null : el('div', { class: 'buy-row' }, [ask]),
    status,
  );
  document.body.append(modal);
  // A dialog behaves like one: a backdrop that dims and blocks the page,
  // role and aria-modal, focus moved in and trapped, Escape to leave, and
  // focus put back where it came from. See ui/modal.js.
  const dialog = asModal(modal, { label: 'A community invitation' });
  return { modal, api: { close } };
}

// ---- The hub section: "Your community" ----------------------------------------------

/**
 * Above "Rooms that are always open": the community you own or belong to,
 * with how many are in it right now and Open. Nothing when you have none and
 * cannot make one; a nudge toward the card when you can.
 * @param {{ client: object, onOpen: (code: string) => void, onManage: () => void }} h
 */
export function renderCommunitySection(h) {
  const list = el('div', { class: 'room-list community-list' });
  const root = el('section', { class: 'room-card browse community-section', hidden: '' }, [
    el('div', { class: 'card-head' }, [
      el('span', { class: 'eyebrow', text: t('Yours') }),
      el('h2', { text: t('Your community') }),
    ]),
    list,
  ]);

  function row(c, isOwner) {
    const next = (c.sessions || [])[0];
    return el('div', { class: 'room-row' }, [
      el('div', { class: 'room-row-main' }, [
        el('div', { class: 'room-name' }, [
          el('span', { class: 'room-name-text', text: c.name }),
          el('span', { class: `room-mode mode-${c.mode || ChatMode.VIDEO}`, text: MODE_LABEL[c.mode] || 'Video' }),
          isOwner ? el('span', { class: 'owner-mark', title: t('Yours'), text: 'HOST' }) : null,
        ].filter(Boolean)),
        el('div', { class: `room-meta${Number(c.here) > 0 ? '' : ' room-quiet'}`, text: hereText(c.here) }),
        next ? el('div', { class: 'room-blurb', text: sessionLine(next) }) : null,
      ].filter(Boolean)),
      el('button', { class: 'btn btn-sm', text: t('Open'), onClick: () => h.onOpen(c.code) }),
    ]);
  }

  function update(mine) {
    list.replaceChildren();
    const owned = mine?.owned || null;
    const memberships = (mine?.memberships || []).filter((m) => m.state === 'member');
    const invitations = mine?.invitations || [];
    if (!owned && !memberships.length && !invitations.length && !mine?.canOwn) {
      root.hidden = true;
      return;
    }
    if (owned) list.append(row(owned, true));
    for (const m of memberships) list.append(row(m, false));
    if (invitations.length) {
      list.append(
        el('div', { class: 'room-row' }, [
          el('div', { class: 'room-row-main' }, [
            el('div', { class: 'room-name' }, [el('span', { class: 'room-name-text', text: invitations.length === 1 ? 'You have an invitation' : `You have ${invitations.length} invitations` })]),
            el('div', { class: 'room-meta', text: invitations.map((i) => i.name).join(', ') }),
          ]),
          el('button', { class: 'btn btn-sm', text: t('See'), onClick: () => h.onManage() }),
        ]),
      );
    }
    if (!owned && mine?.canOwn) {
      list.append(
        el('div', { class: 'room-row' }, [
          el('div', { class: 'room-row-main' }, [
            el('div', { class: 'room-name' }, [el('span', { class: 'room-name-text', text: t('Start a community') })]),
            el('div', { class: 'room-meta room-quiet', text: `A private room of your own for up to ${FREE_MEMBERS} people, still there when you come back.` }),
          ]),
          el('button', { class: 'btn btn-sm', text: t('Start'), onClick: () => h.onManage() }),
        ]),
      );
    }
    root.hidden = false;
  }

  async function refresh() {
    const r = await h.client.mine();
    update(r.status === 200 ? r.data : null);
  }

  refresh();
  return { root, api: { update, refresh } };
}

// ---- The panel inside the room ---------------------------------------------------------
//
// Under the room's chat header: who is a member and who is here now, the next
// session, the invite link, and -- for the owner, while they hold the
// controls -- Remove, Kick, and Rename. Drawn from COMMUNITY_STATE, in full,
// each time one arrives.

/**
 * @param {object} h
 * @param {ReturnType<typeof createCommunityClient>} h.client
 * @param {(peerId: string, reason: string, note: string) => void} h.onKick  the fifteen-minute kick (KICK)
 * @param {() => void} [h.onManage]  open the card for the rest
 */
export function renderCommunityPanel(h) {
  const root = el('div', { class: 'community-panel', hidden: '' });
  let state = null;
  let open = true;

  function memberRow(m) {
    const you = state.you && m.personId === state.you;
    const actions = [];
    if (state.isOwner && !you && m.state !== 'owner') {
      if (m.here && m.peerId) {
        actions.push(el('button', { type: 'button', class: 'btn btn-ghost btn-xs', text: t('Kick'), title: t('Out of the room for 15 minutes; still a member'), onClick: () => promptKick(h.onKick, m.peerId) }));
      }
      if (m.state === 'member') {
        actions.push(
          el('button', {
            type: 'button',
            class: 'btn btn-ghost btn-xs',
            text: t('Remove'),
            title: t('Out of the community: they cannot come back unless you invite them again'),
            onClick: async () => {
              if (!window.confirm(`Remove ${m.handle || 'this person'} from ${state.name}?`)) return;
              const r = await h.client.remove(state.id, m.personId);
              if (r.status !== 200) toast(errorText(r.data?.error));
            },
          }),
        );
      }
      if (m.state === 'requested') {
        actions.push(
          el('button', {
            type: 'button',
            class: 'btn btn-primary btn-xs',
            text: t('Accept'),
            onClick: async () => {
              const r = await h.client.acceptRequest(state.id, m.personId);
              if (r.status !== 200) toast(errorText(r.data?.error));
            },
          }),
        );
      }
    }
    return el('div', { class: `community-row${you ? ' is-you' : ''}` }, [
      el('span', { class: `here-dot${m.here ? ' on' : ''}`, 'aria-hidden': 'true', title: m.here ? 'Here now' : 'Not here right now' }),
      el('span', { class: 'community-row-name', text: you ? `${m.handle || 'You'} (you)` : m.handle || 'Someone' }),
      pill(m.state),
      actions.length ? el('span', { class: 'community-row-actions' }, actions) : null,
    ].filter(Boolean));
  }

  function render() {
    if (!state) {
      root.hidden = true;
      return;
    }
    root.hidden = false;
    const visible = state.members.filter((m) => m.state !== 'requested' && m.state !== 'invited');
    const pending = state.members.filter((m) => m.state === 'requested' || m.state === 'invited');
    const here = visible.filter((m) => m.here).length;
    const next = (state.sessions || [])[0];
    const toggle = el('button', {
      type: 'button',
      class: 'btn btn-ghost btn-xs',
      text: open ? 'Hide' : 'Show',
      aria: { expanded: String(open) },
      onClick: () => {
        open = !open;
        render();
      },
    });
    const rename = state.isOwner
      ? el('button', {
          type: 'button',
          class: 'btn btn-ghost btn-xs',
          text: t('Rename'),
          onClick: async () => {
            const nextName = window.prompt('A new name for your community:', state.name);
            if (nextName == null || !nextName.trim() || nextName.trim() === state.name) return;
            const r = await h.client.rename(state.id, nextName.trim());
            if (r.status !== 200) toast(errorText(r.data?.error));
          },
        })
      : null;
    root.replaceChildren(
      el('div', { class: 'community-panel-head' }, [
        el('span', { class: 'rules-title', text: `Community: ${state.name}` }),
        el('span', { class: 'muted community-panel-count', text: `${visible.length - 1 >= 0 ? memberCountText(visible.length - 1) : ''}, ${here} here` }),
        toggle,
      ]),
      open
        ? el('div', { class: 'community-panel-body' }, [
            next ? el('p', { class: 'community-next', text: `Next: ${sessionLine(next)}` }) : el('p', { class: 'muted community-next', text: t('No session planned.') }),
            el('div', { class: 'community-members' }, visible.map(memberRow)),
            state.isOwner && pending.length ? el('strong', { class: 'community-sub', text: t('Waiting') }) : null,
            state.isOwner && pending.length ? el('div', { class: 'community-members' }, pending.map(memberRow)) : null,
            el('div', { class: 'buy-row community-toolbar' }, [
              copyLinkButton(state.code),
              rename,
              h.onManage ? el('button', { type: 'button', class: 'btn btn-ghost btn-xs', text: state.isOwner ? 'Sessions and invitations' : 'My community', onClick: () => h.onManage() }) : null,
            ].filter(Boolean)),
          ].filter(Boolean))
        : null,
    );
  }

  render();
  return {
    root,
    api: {
      update(next) {
        state = next || null;
        render();
      },
    },
  };
}

export default {
  COMMUNITY_INVITE_COPY,
  ASK_COPY,
  NO_ACCOUNT_COPY,
  stateLabel,
  inviteLink,
  startsIn,
  sessionLine,
  localToIso,
  buildIcs,
  memberCountText,
  hereText,
  errorText,
  createCommunityClient,
  renderCommunityCard,
  openCommunityLanding,
  renderCommunitySection,
  renderCommunityPanel,
};
